import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { capitalizeFirstLetter, globalLoader, getUser, handleServerValidations, setLocalKey, TrimText } from "../../../../utils/commonfunction";
import { categoriesList, subcategoriesList, editBusinessInfo, getUserInfo } from "../../services/account-setting.services";
import { OFFER_PERCENTAGE, ROLE, SWAL_SETTINGS } from "../../../../utils/Constants";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import BusinessPhoneInput from "../../../../utils/businessPhoneInput";
import LocationPicker from "../../../../utils/map";

export default function EditBusinessInfo() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [previewImage, setPreviewImage] = useState("");
  const [brochureImage, setBrochureImage] = useState("");
  const [categoryData, setCategoryData] = useState([]);
  const [subcategoryData, setSubCategoryData] = useState([]);
  const [profileImageError, setProfileImageError] = useState("");
  const [brochureError, setBrochureError] = useState("");
  const [loading, setLoading] = useState(true);

 

  const validationSchema = Yup.object().shape({
    business_name: Yup.string()
      .required("Business name is required")
      .trim()
      .matches(/^[a-zA-Z0-9\s&'-]+$/, "Invalid business name"),
      brochure_title: Yup.string()
      .required("Brochure title is required")
      .trim()
      .matches(/^[a-zA-Z0-9\s&'-]+$/, "Invalid Brochure title"),
    phone_number: Yup.object().shape({
      countryCode: Yup.string().required("Please select a country code"),
      phone: Yup.string()
        .matches(/^[0-9]+$/, "Phone number must contain digits only")
        .min(6, "Phone number must be at least 6 digits")
        .max(12, "Phone number must not exceed 15 digits")
        .required("Phone number is required"),
      fullNumber: Yup.string().required("Full phone number is missing"),
    }),
    address: Yup.string()
      .required("Address is required")
      .trim()

      .matches(/^[a-zA-Z0-9\s&',.-/()#]+$/, "Invalid business address"),
    vat_number: Yup.string()
      .trim()
      .test("vat-length", "VAT number must be exactly 15 characters", (value) => !value || value.length === 15)
      .matches(/^[a-zA-Z0-9\s&'-]+$/, { message: "Invalid VAT number", excludeEmptyString: true }),
    offer_percentage: Yup.string().required("Offer percentage is required"),
    category_id: Yup.string().required("Category is required"),
    subcategory_id: Yup.string().required("Sub category is required"),
    profile_image: Yup.mixed()
  .test(
    "fileType",
    "Only JPG, JPEG, PNG, WEBP, or GIF files are allowed.",
    (value) =>
      !value ||
      typeof value === "string" ||
      ["image/jpg", "image/jpeg", "image/png", "image/webp", "image/gif"].includes(value.type)
  )
  .test(
    "fileSize",
    "File size must be less than 1.5 MB.",
    (value) =>
      !value || typeof value === "string" || value.size <= 1.5 * 1024 * 1024
  )
  .test(
    "isValidUrlOrFile",
    "Invalid URL format.",
    (value) =>
      !value || typeof value !== "string" || /^(ftp|http|https):\/\/[^ "]+$/.test(value)
  ),

brochure: Yup.mixed()
  .test(
    "fileType",
    "Only JPG, JPEG, PNG, WEBP, or GIF files are allowed.",
    (value) =>
      !value ||
      typeof value === "string" ||
      ["image/jpg", "image/jpeg", "image/png", "image/webp", "image/gif"].includes(value.type)
  )
  .test(
    "fileSize",
    "File size must be less than 1.5 MB.",
    (value) =>
      !value || typeof value === "string" || value.size <= 1.5 * 1024 * 1024
  )
  .test(
    "isValidUrlOrFile",
    "Invalid URL format.",
    (value) =>
      !value || typeof value !== "string" || /^(ftp|http|https):\/\/[^ "]+$/.test(value)
  ),

  });

  const formik = useFormik({
    initialValues: {
      business_name: "",
      phone_number: {
        countryCode: "",
        phone: "",
        fullNumber: "",
      },
      address: "",
      vat_number: "",
      offer_percentage: "",
      profile_image: "",
      brochure: "",
      category_id: "",
      subcategory_id: "",
      brochure_title : ""
    },
    validateOnBlur: false,
    validationSchema: validationSchema,
    enableReinitialize: true,

    onSubmit: (values, { resetForm }) => {
   
      globalLoader(true);
      const formData = new FormData();
      formData.append("business_name", values.business_name);
      formData.append("countryCode", values.phone_number.countryCode);
      formData.append("phone_number", values.phone_number.phone);
      formData.append("fullNumber", values.phone_number.fullNumber);

      formData.append("address", values.address);
      formData.append("city", values.city);
      formData.append("state", values.state);
      formData.append("postalCode", values.postalCode);
      formData.append("country", values.country);
      formData.append("lat", values.lat);
      formData.append("lng", values.lng);

      formData.append("vat_number", values.vat_number);
      formData.append("offer_percentage", values.offer_percentage);
      formData.append("category_id", values.category_id);
      formData.append("subcategory_id", values.subcategory_id);
      formData.append("brochure_title", values.brochure_title);
      formData.append("profile_image", values.profile_image);
      formData.append("brochure", values.brochure);


      editBusinessInfo(formData)
        .then((response) => {
          globalLoader(false);
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });

            // Update stored user info (supports encoded localStorage in production)
            const storedUser = getUser(ROLE.BUSINESS);
            if (storedUser && Object.keys(storedUser).length) {
              const updatedUser = {
                ...storedUser,
                business_name: values.business_name,
                phone_number: values.phone_number,
                address: values.address,
                offer_percentage: values.offer_percentage,
                vat_number: values.vat_number,
                category_id: { _id: values.category_id },
                subcategory_id: { _id: values.subcategory_id },
                profile_image: response?.data?.profile_image || storedUser.profile_image,
                brochure: response?.data?.brochure || storedUser.brochure,
                updatedAt: new Date().toISOString(),
                brochure_title: values.brochure_title,
              };
              setLocalKey(ROLE.BUSINESS, JSON.stringify(updatedUser));
            }

            // navigate("/business/account-setting/edit-business-info", { replace: true });
           
          } else {
            console.log(response);
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        })
        .catch((error) => {
          globalLoader(false);
          console.log("error--->", error);
        });
    },
  });

  // Fetch user info and populate form
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        setLoading(true);
        const response = await getUserInfo();
        console.log(response);
        if (response?.success && response?.data) {
          const userData = response.data;

          // Set form values with existing data
          formik.setValues({
            business_name: userData.business_name || "",
            phone_number: {
              countryCode: userData.phone_number?.countryCode || "",
              phone: userData.phone_number?.phone || "",
              fullNumber: userData.phone_number?.fullNumber || "",
            },
            address: userData.address || "",
            vat_number: userData.vat_number || "",
            offer_percentage: userData.offer_percentage || "",
            // Extract the _id from the nested objects for category and subcategory
            category_id: userData.category_id?._id || "",
            subcategory_id: userData.subcategory_id?._id || "",
            brochure_title : userData?.brochure_title || "", 
            profile_image : userData.profile_image||"" , 
            brochure : userData.brochure ||"" , 
            lat :userData.latitude,
            lng :userData.longitude,
            

          });

          // Set existing image previews if they exist
          if (userData.profile_image) {
            setPreviewImage(userData.profile_image);
          }
          if (userData.brochure) {
            setBrochureImage(userData.brochure);
          }
        }
      } catch (error) {
        console.log(error, "thi sis the error log");
        console.log("Error fetching user info:", error.message);
        Swal.fire({
          icon: "error",
          text: "Failed to load business information",
          ...SWAL_SETTINGS,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUserInfo();
  }, []);

  // Fetch categories
  useEffect(() => {
    categoriesList()
      .then((response) => {
        if (response?.success) {
          setCategoryData(response?.data?.list || []);
        }
      })
      .catch((error) => {
        console.log("error", error);
      });
  }, []);

  // Fetch subcategories when category changes
  useEffect(() => {
    if (formik?.values?.category_id) {
      console.log("from this line", formik.values);
      // Use the category_id directly since it's now a string ID
      subcategoriesList({ category: formik.values.category_id })
        .then((response) => {
          if (response?.success) {
            setSubCategoryData(response?.data?.list || []);
          }
        })
        .catch((error) => {
          console.log("error", error);
        });
    } else {
      setSubCategoryData([]);
    }
  }, [formik?.values?.category_id]);

  const handleProfileImageChange = (event) => {
    const file = event.currentTarget.files[0];
    setProfileImageError("");

    if (file) {
      if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
        setProfileImageError("Only JPG, PNG and WEBP files are allowed.");
        formik.setFieldValue("profile_image", "");
        setPreviewImage("");
        return;
      }

      formik.setFieldValue("profile_image", file);
      // Use object URL so the actual image thumbnail is shown
      setPreviewImage(URL.createObjectURL(file));
    } else {
      formik.setFieldValue("profile_image", "");
      setPreviewImage("");
    }
  };

  const handleBrochureChange = (event) => {
    const file = event.currentTarget.files[0];
    setBrochureError("");

    if (file) {
      if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
        setBrochureError("Only JPG, PNG and WEBP files are allowed.");
        formik.setFieldValue("brochure", "");
        setBrochureImage("");
        return;
      }

      formik.setFieldValue("brochure", file);
      // Use object URL so the actual image thumbnail is shown
      setBrochureImage(URL.createObjectURL(file));
    } else {
      formik.setFieldValue("brochure", "");
      setBrochureImage("");
    }
  };

  const removeProfileImage = () => {
    formik.setFieldValue("profile_image", "");
    setPreviewImage("");
    setProfileImageError("");
    const fileInput = document.querySelector('input[name="profile_image"]');
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const removeBrochureImage = () => {
    formik.setFieldValue("brochure", "");
    setBrochureImage("");
    setBrochureError("");
    const fileInput = document.querySelector('input[name="brochure"]');
    if (fileInput) {
      fileInput.value = "";
    }
  };

  if (loading) {
    return (
      <div className="container form-wrapper section-section d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading business information...</p>
        </div>
      </div>
    );
  }

  return (
    <div  className="main-content">
       <div className="customer-part">
    <div className="card-box">
      <div className="text-center">
        <h4 className="h3-headline mb-5">Edit business info</h4>
        <form onSubmit={formik.handleSubmit}>
          <div className="row g-3 text-start">
            <div className="col-md-6">
              <label className="form-label subtitle-1" htmlFor="business_name">
                Business Name<span className="requirestar">*</span>
              </label>
              <input name="business_name" id="business_name" type="text" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.business_name} className="form-control" placeholder={"Please enter business name"} />
              <span className="text-danger d-flex text-left">{formik.touched.business_name && formik.errors.business_name ? <div className="text-danger d-flex text-left">{t(formik.errors.business_name)}</div> : null}</span>
            </div>
            <div className="col-md-6">
              <label className="form-label subtitle-1" htmlFor="category_id">
                Select Category<span className="requirestar">*</span>
              </label>
              <select className="form-control form-select cp" name="category_id" value={formik?.values?.category_id} onChange={(e)=>{formik.handleChange(e); formik.setFieldValue("subcategory_id" , "")}}>
                <option value={""}>{t("Select category")}</option>
                {categoryData.map((item, index) => {
                  return (
                    <option key={index} value={item._id}>
                      {capitalizeFirstLetter(item.title)}
                    </option>
                  );
                })}
              </select>
              {formik.touched.category_id && formik.errors.category_id ? <div className="text-danger d-flex text-left">{t(formik.errors.category_id)}</div> : null}
            </div>
            <div className="col-md-6">
              <label className="form-label subtitle-1" htmlFor="subcategory_id">
                Select Sub Category<span className="requirestar">*</span>
              </label>
              <select id="subcategory_id" className="form-control form-select cp" name="subcategory_id" value={formik?.values?.subcategory_id} onChange={formik.handleChange}>
                <option value={""}>{t("Select subcategory")}</option>
                {subcategoryData.map((item, index) => {
                  return (
                    <option key={index} value={item._id}>
                      {capitalizeFirstLetter(item.title)}
                    </option>
                  );
                })}
              </select>
              {formik.touched.subcategory_id && formik.errors.subcategory_id ? <div className="text-danger d-flex text-left">{t(formik.errors.subcategory_id)}</div> : null}
            </div>
            <div className="col-md-6">
              <label className="form-label subtitle-1" htmlFor="phone_number">
                Phone Number<span className="requirestar">*</span>
              </label>
              <BusinessPhoneInput
                name="phone_number"
                id="phone_number"
                onChange={(phoneObj) => {
                  formik.setFieldValue("phone_number", phoneObj);
                }}
                onBlur={formik.handleBlur}
                value={formik.values.phone_number}
                className="form-control"
                placeholder={t("Enter phone number")}
              />
              <span className="text-danger d-flex text-left">{formik?.touched?.phone_number?.phone && formik?.errors?.phone_number?.phone ? <div className="text-danger d-flex text-left">{t(formik.errors.phone_number.phone)}</div> : null}</span>
            </div>
            <div className="col-md-6 form-group position-relative">
              <label htmlFor="address" className="form-label subtitle-1">
                Business Address<span className="requirestar">*</span>
              </label>

              <input
                name="address"
                id="address"
                type="text"
                value={TrimText(formik.values.address, 25)}
                readOnly // 🔹 prevent manual typing
                className="form-control"
                placeholder={t("Enter business address")}
                data-bs-toggle="modal"
                data-bs-target="#locationModal" // 🔹 clicking opens the map modal
              />
              {console.log(TrimText(formik.values, 25) , 'this is address')}
              {/* Location Picker → will open on input click */}
              <LocationPicker
               onConfirm={(location) => {
                formik.setFieldValue("address", location.address || "");
                formik.setFieldValue("city", location.city || "");
                formik.setFieldValue("state", location.state || "");
                formik.setFieldValue("postalCode", location.postalCode || "");
                formik.setFieldValue("country", location.country || "");
                formik.setFieldValue("lat", location.lat || "");
                formik.setFieldValue("lng", location.lng || "");
              }}
              />

              <span className="text-danger d-flex text-left">
                {formik.touched.address && formik.errors.address ? <div className="text-danger d-flex text-left">{t(Object.values(formik.errors.address)[0])}</div> : null}

               
              </span>
            </div>
            <div className="col-md-6">
              <label className="form-label subtitle-1" htmlFor="vat_number">
                VAT Number
              </label>
              <input name="vat_number" id="vat_number" type="text" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.vat_number} className="form-control" placeholder={t("Enter VAT number")} />
              <span className="text-danger d-flex text-left">{formik.touched.vat_number && formik.errors.vat_number ? <div className="text-danger d-flex text-left">{t(formik.errors.vat_number)}</div> : null}</span>
            </div>

            <div className="col-12">
              <label className="form-label subtitle-1" htmlFor="offer_percentage">
                Select Offer Percentage<span className="requirestar">*</span>
              </label>
              <select className="form-control form-select cp" id="offer_percentage" name="offer_percentage" value={formik?.values?.offer_percentage} onChange={formik.handleChange}>
                <option value={""}>{t("Select")}</option>
                {OFFER_PERCENTAGE.map((item, index) => {
                  return (
                    <option key={index} value={item.value}>
                      {item.label}
                    </option>
                  );
                })}
              </select>
              {formik.touched.offer_percentage && formik.errors.offer_percentage ? <div className="text-danger d-flex text-left">{t(formik.errors.offer_percentage)}</div> : null}
            </div>
            <div className="col-12">
              <label className="form-label subtitle-1" htmlFor="profile_image">
                Business Photo
              </label>

              {!previewImage ? (
                <div className="custom-file-upload">
                  <div className="custom-upload-btn">
                    <span>Browse</span>
                    <i className="fi fi-br-upload" />
                  </div>
                  <input type="file" id="profile_image" name="profile_image" accept="image/jpeg, image/png, image/webp" onChange={handleProfileImageChange} />
                </div>
              ) : (
                <div className="mt-2 d-flex align-items-center gap-3 p-2 bg-light rounded">
                  <img
                    src={previewImage}
                    alt="Business Photo Preview"
                    style={{ width: "64px", height: "64px", objectFit: "cover", borderRadius: "8px", border: "1px solid #ddd" }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <span className="text-truncate flex-grow-1 small text-muted">
                    {formik.values.profile_image?.name || "Image selected"}
                  </span>
                  <button type="button" className="btn btn-sm btn-outline-danger ms-auto" onClick={removeProfileImage} title="Remove image">
                    <i className="fi fi-rr-cross-small" style={{ position: "relative", top: "2px" }}></i>
                  </button>
                </div>
              )}

              {profileImageError && <div className="text-danger d-flex text-left">{profileImageError}</div>}
              {!profileImageError && formik.touched.profile_image && formik.errors.profile_image && <div className="text-danger d-flex text-left">{t(formik.errors.profile_image)}</div>}
            </div>

            <div className="col-md-12">
              <label className="form-label subtitle-1" htmlFor="brochure_title">
              Brochure Title<span className="requirestar">*</span>
              </label>
              <input name="brochure_title" id="brochure_title" type="text" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.brochure_title} className="form-control" placeholder={t("Enter Brochure Title")} />
              <span className="text-danger d-flex text-left">{formik.touched.brochure_title && formik.errors.brochure_title ? <div className="text-danger d-flex text-left">{t(formik.errors.brochure_title)}</div> : null}</span>
            </div>

            <div className="col-12 mt-3">
              <label className="form-label subtitle-1" htmlFor="upload_brochure">
                Upload Brochure
              </label>

              {!brochureImage ? (
                <div className="custom-file-upload">
                  <div className="custom-upload-btn">
                    <span>Browse</span>
                    <i className="fi fi-br-upload" />
                  </div>
                  <input id="upload_brochure" type="file" name="brochure" accept="image/jpeg, image/png, image/webp" onChange={handleBrochureChange} />
                </div>
              ) : (
                <div className="mt-2 d-flex align-items-center gap-3 p-2 bg-light rounded">
                  <img
                    src={brochureImage}
                    alt="Brochure Preview"
                    style={{ width: "64px", height: "64px", objectFit: "cover", borderRadius: "8px", border: "1px solid #ddd" }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <span className="text-truncate flex-grow-1 small text-muted">
                    {formik.values.brochure?.name || "Brochure selected"}
                  </span>
                  <button type="button" className="btn btn-sm btn-outline-danger ms-auto" onClick={removeBrochureImage} title="Remove brochure">
                    <i className="fi fi-rr-cross-small" style={{ position: "relative", top: "2px" }}></i>
                  </button>
                </div>
              )}

              {brochureError && <div className="text-danger d-flex text-left">{brochureError}</div>}
              {!brochureError && formik.touched.brochure && formik.errors.brochure && <div className="text-danger d-flex text-left">{t(formik.errors.brochure)}</div>}
            </div>

            <div className="col-md-12 text-center mt-3">
              <button type="submit" className="btn btn-light-purple px-5">
                Update
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
    </div>
    </div>
  );
}
